/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Paper, Stepper, Step, StepLabel, StepContent } from '@material-ui/core';
import { toast } from 'react-toastify';
import { MENU_WIDTH } from '../constants';
import Prepare from '../components/AddDevice/Prepare';
import Cable from '../components/AddDevice/Cable';
import Confirm from '../components/AddDevice/Confirm';
import MacAddress from '../components/AddDevice/MacAddress';
import Settings from '../components/AddDevice/Settings';
import WifiSetup from '../components/AddDevice/WifiSetup';
import CertificationSetup from '../components/AddDevice/CertificationSetup';
import MqttSetup from '../components/AddDevice/MqttSetup';
import Complete from '../components/AddDevice/Complete';
import { provisionDevice } from '../store/actions/devices';
import Scripts from '../components/AddDevice/Scripts';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    paddingLeft: MENU_WIDTH,
    paddingTop: '6rem',
  },
  title: {
    margin: '2rem 0 0 2rem',
  },
  paper: {
    margin: '2em',
    height: '100%',
  },
  stepContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  buttonRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  error: {
    color: 'red',
    marginLeft: '1rem',
  },
  button: {
    marginLeft: '2em',
  },
}));

const AddDevice = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [nextDisabled, setNextDisabled] = useState(false);
  const [macAddress, setMacAddress] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const devicesList = useSelector((state) => state.devices.devicesList);
  const stepLabels = [
    'Identify Monitor',
    'Prepare Scripts',
    'Prepare Monitor for Connection',
    'Insert Cable',
    'Input Settings',
    'Wifi Connection',
    'Certificate Installation',
    'MQTT Connection',
    'Confirm Successful Connection',
    'Complete',
  ];
  const stepContents = [
    <MacAddress macAddress={macAddress} setMacAddress={setMacAddress} />,
    <Scripts />,
    <Prepare />,
    <Cable />,
    <Settings />,
    <WifiSetup />,
    <CertificationSetup />,
    <MqttSetup />,
    <Confirm isConfirmed={isConfirmed} setIsConfirmed={setIsConfirmed} macAddress={macAddress} />,
    <Complete />,
  ];

  useEffect(() => {
    if (activeStep === 0 && macAddress.length !== 6) setNextDisabled(true);
    else if (activeStep === 8 && !isConfirmed) setNextDisabled(true);
    else setNextDisabled(false);
  }, [activeStep, macAddress, isConfirmed]);

  // eslint-disable-next-line consistent-return
  const handleNext = () => {
    if (activeStep === 0 && macAddress) {
      const match = devicesList.find((findDevice) => findDevice.macAddress === macAddress);
      if (match) {
        toast.error('A monitor with this monitor ID has already been set up.', {
          position: 'bottom-left',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          theme: 'colored',
        });
        return false;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleDone = () => {
    dispatch(provisionDevice(macAddress));
    history.push('./monitors');
    toast.success(`Monitor has been set up.`, {
      position: 'bottom-left',
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      theme: 'colored',
    });
  };

  return (
    <div className={classes.root}>
      <h2 className={classes.title}>Add Monitor</h2>
      <Paper className={classes.paper}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {stepLabels.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                <div className={classes.stepContent}>{stepContents[index]}</div>
                <div className={classes.buttonRow}>
                  {index !== 0 && (
                    <Button className={classes.button} onClick={handleBack}>
                      Back
                    </Button>
                  )}
                  {index < stepLabels.length - 1 && (
                    <Button
                      className={classes.button}
                      disabled={nextDisabled}
                      color="primary"
                      variant="contained"
                      onClick={handleNext}
                    >
                      Continue
                    </Button>
                  )}
                  {index === stepLabels.length - 1 && (
                    <Button className={classes.button} color="primary" variant="contained" onClick={handleDone}>
                      Done
                    </Button>
                  )}
                </div>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </div>
  );
};

export default AddDevice;
